// Pure geometry and mapping for the PA Week grid (`calendar-week-grid.tsx`), mirroring Antonin's export
// (`src/components/CalendarGrid/CalendarGrid.tsx` week body, `src/components/AppointmentTile/AppointmentTile.tsx`) and the
// drag-to-reschedule of the Signal DS `PACalendarWeek` (DS 2.29.1 `dist/index.js` `PACalendarWeek.handleDragEnd`).
import type { PACalendarAppointment, PACalendarAppointmentDropPayload } from '@scripsteam/scrips-signal-ds';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import { formatHourMark } from '@/shared/lib/time-format';

/** One hour of the grid, in px (export `HOUR_PX`, DS `PACalendarWeek` 80). The CSS hour row is the same 80
 *  (`--cal-week-hour` in `showcase-calendar-week.css`); `calendar-page.tsx` scrolls to now with the same figure. */
export const WEEK_HOUR_PX = 80;
/** Air between a tile and the next above or below: the export grid's practitioner tone (`TILE_GAP.practitioner`). */
export const WEEK_TILE_GAP = 2;
/** The shortest tile drawn (export and DS: 24). */
export const WEEK_TILE_MIN_HEIGHT = 24;
/** Under this height the tile is one line (export `COMPACT_BELOW`). */
export const WEEK_TILE_COMPACT_BELOW = 44;
/** The note line shows only on a taller tile (export, practice tile: `height > 56`). */
export const WEEK_TILE_NOTE_ABOVE = 56;
/** The DS default gutter when `hours` is omitted: 8 a.m. to 5 p.m. (DS `PACalendarWeek` `DEFAULT_HOURS`). */
export const WEEK_DEFAULT_HOURS: readonly number[] = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17];

/** The export's three location kinds (tile tint and edge colour). */
export type TileLocation = 'office' | 'home' | 'virtual';
/** The export's six statuses (DS-023), what the tile's glyph draws. */
export type TileStatus = 'in-session' | 'arrived' | 'completed' | 'no-show' | 'booked' | 'cancelled';

/** An appointment's location kind from its location name ("Office", "Home visit", "Virtual" …): the same reading as the
 *  Day rows' location hue (`appointment-glyphs.ts` `locationHue`). Unknown or absent reads as in clinic. */
export function tileLocation(name: string | null | undefined): TileLocation {
  const n = (name ?? '').toLowerCase();
  if (n.includes('virtual') || n.includes('video') || n.includes('tele')) return 'virtual';
  if (n.includes('home')) return 'home';
  return 'office';
}

/** The DS appointment status (the API code, mapped by `calendar-mapping.ts`) to the export's six, as the export's own
 *  calendar demo maps them (`screens/practitioner/demo/calendar.ts` `FINE`): checked in is arrived, confirmed is booked. */
export function tileStatus(status: PACalendarAppointment['status']): TileStatus {
  switch (status) {
    case 'arrived':
    case 'checkedIn':
      return 'arrived';
    case 'fulfilled':
      return 'completed';
    case 'noShow':
      return 'no-show';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'booked';
  }
}

/** The status word for the tile's accessible name: the PA's finer stages keep their own word. */
export function tileStatusWord(status: PACalendarAppointment['status']): string {
  switch (status) {
    case 'arrived':
      return 'Arrived';
    case 'checkedIn':
      return 'Checked in';
    case 'confirmed':
      return 'Confirmed';
    case 'fulfilled':
      return 'Completed';
    case 'noShow':
      return 'No show';
    case 'cancelled':
      return 'Cancelled';
    case 'waitlisted':
      return 'Waitlisted';
    default:
      return 'Booked';
  }
}

/** Top and height of a tile in its day column (export `TimeBody`: the gap split above and below, 24 at least). */
export function tileGeometry(apt: Pick<PACalendarAppointment, 'startHour' | 'startMin' | 'durationMinutes'>, firstHour: number) {
  return {
    top: (apt.startHour - firstHour) * WEEK_HOUR_PX + (apt.startMin / 60) * WEEK_HOUR_PX + WEEK_TILE_GAP / 2,
    height: Math.max((apt.durationMinutes / 60) * WEEK_HOUR_PX - WEEK_TILE_GAP, WEEK_TILE_MIN_HEIGHT),
  };
}

/** Side-by-side columns for bookings that overlap in time (export `layoutOverlaps`: greedy column packing per overlap
 *  cluster). Only bookings that share a cluster with another get a slot narrower than the column. */
export function layoutOverlaps(items: readonly Pick<PACalendarAppointment, 'id' | 'startHour' | 'startMin' | 'durationMinutes'>[]) {
  const sorted = [...items].sort((a, b) => a.startHour * 60 + a.startMin - (b.startHour * 60 + b.startMin));
  const out = new Map<string, { left: number; width: number }>();
  let cluster: { id: string; col: number; end: number }[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const cols = Math.max(1, ...cluster.map((c) => c.col + 1));
    cluster.forEach((c) => out.set(c.id, { left: (c.col / cols) * 100, width: 100 / cols }));
    cluster = [];
  };
  for (const b of sorted) {
    const start = b.startHour * 60 + b.startMin;
    if (cluster.length && start >= clusterEnd) {
      flush();
      clusterEnd = -1;
    }
    const taken = new Set(cluster.filter((c) => c.end > start).map((c) => c.col));
    let col = 0;
    while (taken.has(col)) col += 1;
    const end = start + b.durationMinutes;
    cluster.push({ id: b.id, col, end });
    clusterEnd = Math.max(clusterEnd, end);
  }
  flush();
  return out;
}

/** True once the booking's end has passed (export `past`: the tile steps back to grey). No start, never past. */
export function isTilePast(apt: Pick<PACalendarAppointment, 'startISO' | 'durationMinutes'>, now: Date): boolean {
  if (!apt.startISO) return false;
  const start = new Date(apt.startISO).getTime();
  if (Number.isNaN(start)) return false;
  return start + apt.durationMinutes * 60_000 <= now.getTime();
}

/** The droppable id of one half-hour cell (DS `HalfHourCell`: `cell-{dayIndex}-{hour}-{half}`). */
export function halfCellId(dayIndex: number, hour: number, half: 0 | 1): string {
  return `cell-${dayIndex}-${hour}-${half}`;
}

/**
 * The reschedule payload for a tile dropped on a half-hour cell, exactly as the DS `PACalendarWeek` builds it: the
 * target hour and half (00 or 30) on the booking's own date, written as the no-zone wire datetime. `null` when the
 * drop is a no-op: no cell, another day (cross-day drag is out of part 1), the same half-hour, or the same wire string.
 */
export function weekDropPayload(
  apt: Pick<PACalendarAppointment, 'id' | 'dayIndex' | 'startHour' | 'startMin' | 'startISO'> | undefined,
  overId: string | number | null | undefined,
  now: Date = new Date(),
): PACalendarAppointmentDropPayload | null {
  if (!apt || overId == null) return null;
  const match = String(overId).match(/^cell-(\d+)-(\d+)-(\d+)$/);
  if (!match) return null;
  const dayIndex = Number(match[1]);
  const targetHour = Number(match[2]);
  const targetHalf = Number(match[3]);
  if (apt.dayIndex !== dayIndex) return null;
  const sourceHalf = apt.startMin < 30 ? 0 : 1;
  if (apt.startHour === targetHour && sourceHalf === targetHalf) return null;
  const base = apt.startISO ? new Date(apt.startISO) : new Date(now.getTime());
  base.setHours(targetHour, targetHalf === 0 ? 0 : 30, 0, 0);
  const newStartISO = formatLocalDatetimeForWire(base);
  if (apt.startISO && formatLocalDatetimeForWire(apt.startISO) === newStartISO) return null;
  return { id: apt.id, newStartISO, dayIndex };
}

/** Hour label in the viewer's 12/24-hour format, "7a" / "07:00" (shared/lib/time-format.ts, PROD-3313). */
export function hourMark(hour: number): string {
  return formatHourMark(hour);
}
